/**
 * What is in the hold, from the cockpit.
 *
 * Cargo used to be something you could only see in a berth, which is exactly
 * backwards for the one thing about it that changes while you are flying: a
 * delivery has a clock on it. Being unable to check how long is left on a crate
 * without landing made the deadline something you either remembered or lost.
 *
 * ── Deliveries first, and they are the reason this exists ───────────────────
 *
 * Ordinary goods are a list of weights and can stay one. A delivery carries a
 * destination, a countdown and a reward, and each of those is a thing you might
 * change course over — so it gets a line of its own and a button that points
 * the ship at it.
 *
 * Toggled rather than always up. Most of a flight is not about the hold.
 */

import {
  cargoWeight,
  formatRemaining,
  missionExpired,
  missionRemaining,
  type CargoItem,
} from '@interspacial/shared';
import { HUD_DIM, HUD_EDGE } from './FlightHud';
import { pinTarget } from './SearchPanel';

/**
 * Whether anything aboard is running out of time.
 *
 * Exported because the *button* needs it and the button is on the console —
 * the whole point of the hold being in the cockpit is that a deadline can be
 * noticed without opening anything.
 */
export function holdUrgent(cargo: readonly CargoItem[], now: number): boolean {
  return cargo.some((item) => {
    if (item.mission === undefined) return false;
    if (missionExpired(item.mission, now)) return false;

    // Null is the introduction's crate, which has no deadline and therefore
    // cannot be running out of one. A job that cannot expire is never urgent.
    const left = missionRemaining(item.mission, now);
    return left !== null && left < 300;
  });
}

/**
 * The hold as a line of text, for the console's typed body.
 *
 * The weight and the plain goods say all they have to say as a sentence, and a
 * sentence is what the console can type. Deliveries do not — they carry a
 * countdown and a button — so they stay below as real markup. See `HoldContents`.
 */
export function holdSummary(cargo: readonly CargoItem[], capacity: number): string {
  const used = cargoWeight(cargo);
  const load = `${used.toFixed(1)} of ${capacity}w`;

  if (cargo.length === 0) return `Empty. ${load}.`;

  const deliveries = cargo.filter((item) => item.mission !== undefined).length;
  const goods = cargo.length - deliveries;

  const parts = [load];
  if (goods > 0) parts.push(`${goods} ${goods === 1 ? 'lot' : 'lots'} stowed`);
  if (deliveries > 0) parts.push(`${deliveries} ${deliveries === 1 ? 'job' : 'jobs'} aboard`);
  if (used > capacity) parts.push('OVERLOADED');

  return `${parts.join(' · ')}.`;
}

/**
 * The hold's contents, with no frame of its own.
 *
 * Split from the panel that used to wrap it so the console can print it as one
 * section among several — the frame, the header and the weight are the console's
 * job now, and having them here as well is what made three panels look like
 * three unrelated windows.
 */
export function HoldContents({ cargo, now }: { cargo: readonly CargoItem[]; now: number }) {
  const deliveries = cargo.filter((item) => item.mission !== undefined);

  return (
    <>

      {/* ── Jobs ───────────────────────────────────────────────────────── */}
      {deliveries.map((item) => {
        const mission = item.mission!;
        const dead = missionExpired(mission, now);
        const left = missionRemaining(mission, now);

        return (
          <div
            key={item.id}
            style={{
              borderTop: `1px solid ${HUD_EDGE}`,
              paddingTop: 6,
              marginTop: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <div style={{ fontSize: 11.5 }}>{item.label}</div>
            <div style={{ fontSize: 10.5, color: HUD_DIM }}>
              → {mission.destination.stationName}, {mission.destination.systemName}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  flex: 1,
                  fontSize: 10.5,
                  fontVariantNumeric: 'tabular-nums',
                  // No deadline reads as calm rather than as urgent: `left` is
                  // null for the introduction's crate, and null is not < 300.
                  color: dead ? '#ff5f6b' : left !== null && left < 300 ? '#ffb347' : HUD_DIM,
                }}
              >
                {dead ? 'expired' : formatRemaining(left)} · {mission.reward}L
              </span>

              {/* Points the ship at it. The same pin the chart and the mission
                  board use, so all three mean one thing by "target". */}
              {!dead && (
                <button
                  type="button"
                  onClick={() =>
                    pinTarget({
                      kind: 'body',
                      label: mission.destination.stationName,
                      detail: `delivery · ${mission.destination.systemName}`,
                      systemId: mission.destination.systemId,
                      clusterId: mission.destination.systemId.split('-')[0] as never,
                      bodyName: mission.destination.stationName,
                    })
                  }
                  style={routeStyle}
                >
                  route
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Everything else ────────────────────────────────────────────── */}
      {cargo
        .filter((item) => item.mission === undefined)
        .map((item) => (
          <div
            key={item.id}
            style={{ display: 'flex', gap: 6, fontSize: 11, color: HUD_DIM, marginTop: 4 }}
          >
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
            <span>×{item.count}</span>
          </div>
        ))}
    </>
  );
}

const routeStyle: React.CSSProperties = {
  background: 'rgba(190,120,255,0.16)',
  border: '1px solid rgba(190,120,255,0.45)',
  color: '#e8d8ff',
  font: 'inherit',
  fontSize: 10.5,
  padding: '2px 8px',
  cursor: 'pointer',
};
